import mixpanel, { Dict } from 'mixpanel-browser';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { safeStorage } from 'common/utils/safeStorage';
import { mixpanelToken as token } from '../network/endpoints';

if (token) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  mixpanel.init(token, { track_pageview: true, api_host: 'https://api-eu.mixpanel.com' });
}

// The store build on every event, so any report can be split by app version
// (e.g. 1.150 users against people still on an older build in the same
// weeks). Native only: the web app has no store version. Kept here so
// reset(), which clears every super property, can put it straight back.
let appVersionProps: Dict | null = null;
const registerAppVersion = (): void => {
  if (!token || !appVersionProps) return;
  try {
    mixpanel.register(appVersionProps);
  } catch {
    // tracking must never throw into product code
  }
};
if (token && Capacitor.isNativePlatform() && typeof App?.getInfo === 'function') {
  try {
    App.getInfo()
      .then(({ version, build }) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        appVersionProps = { app_version: version, app_build: build };
        registerAppVersion();
      })
      .catch(() => {
        // no version: events simply go without it
      });
  } catch {
    // tracking must never throw into product code
  }
}

export type InstallAttribution = {
  ad_network?: string;
  ad_campaign?: string;
  ad_group?: string;
  ad_creative?: string;
  ad_tracker_name?: string;
};

const ATTRIBUTION_KEY = 'install_attribution';

// Profile property names are prefixed "Install" so they read clearly in the Mixpanel UI.
const applyStoredAttribution = () => {
  try {
    const raw = safeStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return;
    const a = JSON.parse(raw) as InstallAttribution;
    const props: Dict = {};
    if (a.ad_network) props['Install - Ad Network'] = a.ad_network;
    if (a.ad_campaign) props['Install - Ad Campaign'] = a.ad_campaign;
    if (a.ad_group) props['Install - Ad Group'] = a.ad_group;
    if (a.ad_creative) props['Install - Ad'] = a.ad_creative;
    if (a.ad_tracker_name) props['Install - Tracker'] = a.ad_tracker_name;
    if (Object.keys(props).length) mixpanel.people.set(props);
  } catch {
    // tracking must never throw into product code
  }
};

// Runs after every identify with the new distinct id. Registered by app.tsx once
// RevenueCat is configured, so RevenueCat's Mixpanel integration files its
// server-side events (trial started, converted, renewed) under the same identity
// the app tracks against. Kept as a listener so this module stays free of the
// payment SDKs.
let identifyListener: ((id: string) => void) | null = null;

const identify = (id: string) => {
  mixpanel.identify(id);
  try {
    identifyListener?.(id);
  } catch {
    // tracking must never throw into product code
  }
};

const actions = {
  identify,
  setIdentifyListener: (listener: ((id: string) => void) | null) => {
    identifyListener = listener;
  },
  alias: (id: string) => {
    mixpanel.alias(id);
  },
  // Sign-up success: the one place a new account is joined to the anonymous
  // activity that led to it. The project uses the original ID Merge API, where
  // only an anonymous id can be merged, so guests are never identified (they
  // run on Mixpanel's own $device: id) and the join happens here, once.
  // Alias only when the device is still anonymous, or still carries a guest
  // token identified by a build from before this change; otherwise (someone
  // else was signed in on this device) aliasing would merge two people, so
  // identify only. Shares aliasOnce's flag so a later login does not repeat it.
  linkNewAccount: (userId: string, { wasGuestSignup }: { wasGuestSignup: boolean }) => {
    try {
      const current = mixpanel.get_distinct_id();
      const anonymousDevice = typeof current === 'string'
        && current.startsWith('$device:')
        && !mixpanel.get_property('$user_id');
      const legacyGuest = wasGuestSignup && typeof current === 'string' && /^[0-9a-f]{64}$/i.test(current);
      const flag = `mp_aliased_${userId}`;
      if ((anonymousDevice || legacyGuest) && !safeStorage.getItem(flag)) {
        mixpanel.alias(userId);
        safeStorage.setItem(flag, '1');
      }
    } catch {
      // tracking must never throw into product code
    }
    identify(userId);
  },
  // Mixpanel rejects double-alias with HTTP 400; the localStorage flag prevents repeats per device per userId.
  aliasOnce: (userId: string) => {
    const flag = `mp_aliased_${userId}`;
    try {
      if (safeStorage.getItem(flag)) return;
    } catch {
      return;
    }
    try {
      mixpanel.alias(userId);
      safeStorage.setItem(flag, '1');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[mixpanel] aliasOnce failed', e);
    }
  },
  getDistinctId: (): string | undefined => {
    try {
      return mixpanel.get_distinct_id();
    } catch {
      return undefined;
    }
  },
  track: (name: string, props: Dict = {}) => {
    mixpanel.track(name, props);
  },
  // Super properties: attached to every subsequent event from this device.
  register: (props: Dict) => {
    mixpanel.register(props);
  },
  // Same, for values that can come and go (guest_token, onboarding_variant):
  // a null or undefined value removes that super property instead of
  // registering it, so a cleared guest session stops stamping later events.
  registerSuper: (props: Dict): void => {
    try {
      const set: Dict = {};
      Object.entries(props).forEach(([key, value]) => {
        if (value === null || value === undefined) mixpanel.unregister(key);
        else set[key] = value;
      });
      if (Object.keys(set).length) mixpanel.register(set);
    } catch {
      // tracking must never throw into product code
    }
  },
  // Sign-out: forget the account on this device so whoever uses it next
  // (often a new guest) starts on a fresh anonymous $device: id instead of
  // being tracked on, and later merged into, the previous user. Clears every
  // super property; GuestAnalyticsSync re-registers the guest ones on the
  // next render. Does not hand the new id to RevenueCat: on a 401 or
  // cold-start logout Purchases.logOut may not have finished, so the new id
  // could land on the previous user's RevenueCat customer. The paywall shares
  // the device id with RevenueCat for guests before any purchase instead.
  // Called from clearCredentials only when someone was signed in.
  reset: (): void => {
    try {
      mixpanel.reset();
    } catch {
      // tracking must never throw into product code
    }
    // The device's build has not changed: keep stamping it.
    registerAppVersion();
  },
  // Hands the current distinct id to the identify listener (RevenueCat)
  // without identifying. Guests are never identified, so this is how
  // RevenueCat learns the guest's $device: id before a purchase.
  shareDistinctId: (): void => {
    try {
      const id = mixpanel.get_distinct_id();
      if (id) identifyListener?.(id);
    } catch {
      // tracking must never throw into product code
    }
  },
  people: {
    set: (props: Dict) => {
      mixpanel.people.set(props);
    },
  },
  // Mixpanel cohort exports match people in OneSignal through this profile property
  // (OneSignal's external_id is the same Doqit user id). Called whenever we identify.
  linkOneSignal: (userId: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      mixpanel.people.set({ $onesignal_user_id: userId });
      applyStoredAttribution();
    } catch {
      // tracking must never throw into product code
    }
  },
  // Where the install came from (Adjust), as profile properties so Mixpanel cohorts
  // like "came from a Meta campaign and never added a document" can sync to OneSignal.
  // Adjust answers at launch, often before sign-in, so it is kept on the device and
  // re-applied whenever the signed-in user is identified.
  setAttribution: (attribution: InstallAttribution) => {
    try {
      const clean = Object.fromEntries(Object.entries(attribution).filter(([, v]) => typeof v === 'string' && v !== ''));
      if (Object.keys(clean).length === 0) return;
      safeStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(clean));
      applyStoredAttribution();
    } catch {
      // tracking must never throw into product code
    }
  },
};

export const Mixpanel = actions;
